import { ChevronRight, FileText, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useAssetVersionQueries,
  useLibraryAssetsQuery,
  useLibraryPartManifestQuery,
  type LibraryAsset,
  type LibraryAssetVersionDetail,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { isPartsContent } from "@/lib/assetContent";
import { EM_DASH, formatDate } from "@/lib/format";
import { manifestFor, requiredRoles } from "@/lib/namedParts";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import {
  EmptyState,
  LoadingState,
  PartialDataNotice,
  StaleDataNotice,
} from "@/ui/states";

import { AssetStateBadges } from "../AssetStateBadges";
import { LibrarySectionHeader } from "../LibrarySectionHeader";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewMainConcernDialog } from "./NewMainConcernDialog";

/**
 * The canonical Main-concern catalog (#492).
 *
 * Name · Opening prompt · Updated · Adoption, exactly as the canonical reference
 * lists them, in place of the generic catalog's Asset · State · Version · Author
 * · Updated · Adoption.
 *
 * THE OPENING PROMPT IS A CONTENT READ, and that is the one thing about this
 * surface worth explaining. `GET /library/assets` carries no content by
 * contract, so the column cannot come from the catalog list. It comes from
 * `useAssetVersionQueries`, the batch primitive #369 already built for exactly
 * this shape of question — one head version per row, sharing
 * `queryKeys.assetVersion` with the editor, so opening a row afterwards reads
 * what this already fetched rather than fetching again.
 *
 * WHICH document is the opening prompt is the SERVICE's answer, not a guess: it
 * is the first required prompt-kind role in the manifest for `main_concern`. A
 * row whose content has not arrived, or whose asset carries no such document,
 * shows an em dash rather than a plausible-looking blank.
 *
 * AND A ROW WHOSE READ FAILED SAYS SO. An em dash means "this asset has no
 * opening prompt", which is a fact about the content; a failed head read is a
 * fact about the READ, and rendering both the same way hid partial data in a
 * canonical content column with no way to recover it. `useAssetVersionQueries`
 * classifies per asset — pending, failed, paused — and this surface reads that
 * classification rather than collapsing everything that is not a success into
 * `null`.
 *
 * ARCHIVED DISCOVERY IS DELIBERATE (#647, audit MC-LIB-10). The default read
 * stays active-only, exactly as the service defaults it; `Show archived` is
 * the same control the generic catalog offers, and ONE state variable drives
 * the query, the paused-retry key, the control's `aria-pressed`, AND its
 * visible variant, so what the button claims and what the table shows cannot
 * disagree — for a screen reader and for a sighted reader alike. The visible
 * half matters most when the section has no archived rows at all: the row set
 * does not change, so the control is the only thing that can say which filter
 * is in force. The audit already recorded the generic control as "unknowable
 * from its label" (PW-LIB-09); this surface does not inherit that. State and
 * version live IN the Name cell rather than as columns: the canonical column
 * set above is #492's ruling and stands, but a mixed active/archived list must
 * mark which rows are which — `AssetStateBadges` is server-derived truth, not
 * a client election. They sit on a SECOND LINE under the name rather than
 * beside it: beside it, they competed with the name for one narrow track and
 * won, leaving the link a few pixels wide at ordinary container widths.
 * Lifecycle actions stay on the asset page: this ticket is discovery, and
 * restoration is gated there — and on the service — by `library.restore`, the
 * admin/operations capability the ratified contract (§2.1) reserves.
 *
 * THE HEAD READS ARE BOUNDED. Every row costs one version-detail read, and an
 * archive is never deleted, so "Show archived" over a catalog that has been
 * curated for a year would have fanned out to every main concern that ever
 * existed in one click. The table renders a page at a time (`PAGE_SIZE`),
 * reads heads only for the rows on screen, and offers the next page as a
 * control — so the request count per click is `1 + pageSize` whatever the
 * catalog's size. View state only: the service's answer is unchanged, the rows
 * are the same rows in the same order, and the count line says how many of
 * them are on screen.
 */

/**
 * Column tracks for the wide table, shared by the header and every row.
 *
 * Gated on CONTAINER width, not viewport, and at `@5xl` (64rem) rather than
 * `@3xl` for the reason `LibraryCatalog` states: beside the application
 * sidebar and the library rail this card is narrower than the window, and
 * below 64rem the fixed tracks plus gaps leave the name track too little to
 * show a name. Below `@5xl` the rows keep the stacked layout, which cannot
 * overlap at any width; the name track has a real minimum so it can never be
 * squeezed to nothing by the prompt column's share.
 */
const COLUMNS =
  "@5xl:grid-cols-[minmax(12rem,1.6fr)_minmax(0,2fr)_minmax(7rem,1fr)_6rem_2.5rem]";

/** Rows shown per page, and so the ceiling on head reads per click. */
const PAGE_SIZE = 20;

export function MainConcernCatalog({
  sectionLabel,
  sectionDescription,
  pageSize = PAGE_SIZE,
}: {
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
  /**
   * The page window. A prop only so a test can drive the paging with a
   * handful of rows; the product renders the default.
   */
  pageSize?: number;
}) {
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<LibraryAsset | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // Pages revealed so far. Reset whenever the row set changes shape (the
  // archived toggle), so one click never carries an expanded window into a
  // larger population.
  const [pages, setPages] = useState(1);
  // The row whose link should take focus once the next page has rendered —
  // the first newly revealed one. Keyboard users otherwise stay on a control
  // that now sits BELOW the rows it just added (Tab skips them), and lose
  // focus to `body` when the last page removes the control entirely.
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const rowLinks = useRef(new Map<string, HTMLAnchorElement>());
  const assets = useLibraryAssetsQuery(showArchived);
  const manifest = useLibraryPartManifestQuery();
  const write = useCapability("library.write");

  const items = (assets.data?.items ?? []).filter(
    (asset) => asset.assetType === "main_concern",
  );
  const visible = items.slice(0, pages * pageSize);
  const hidden = items.length - visible.length;
  // Paging is in force at all once the population exceeds one page; the
  // footer then stays MOUNTED through the last reveal so its status line
  // can announce "all shown" and nothing focused is ever unmounted.
  const paged = items.length > pageSize;

  useEffect(() => {
    if (focusIndex === null) return;
    const target = visible[focusIndex];
    const link = target ? rowLinks.current.get(target.id) : undefined;
    if (link) {
      link.focus();
      setFocusIndex(null);
    }
  }, [focusIndex, visible]);

  // Heads for the rows ON SCREEN only — the bound the header comment promises.
  const heads = useAssetVersionQueries(
    visible.map((asset) => ({ assetId: asset.id, version: asset.latestVersion })),
  );

  const entry = manifestFor(manifest.data, "main_concern");
  const openingRole =
    requiredRoles(entry).find((definition) => definition.kind === "prompt_text")
      ?.role ?? null;

  // THE MANIFEST IS A DEPENDENCY OF THIS COLUMN, not merely of the create path.
  //
  // WHICH document is the opening prompt is the manifest's answer. With that
  // read unavailable, `openingRole` is null and `openingPromptFor` returns null
  // for every row — including rows whose own head read succeeded perfectly — so
  // the column rendered an em dash, which in this table means "this asset has no
  // opening prompt". The browser has not learned which role that is; it cannot
  // truthfully say the prompt is absent. `roleUnknown` is what keeps the column
  // honest about the difference.
  const manifestFailure = manifest.isPaused
    ? manifest.failureReason
    : manifest.isError
      ? manifest.error
      : null;
  const roleUnknown = manifest.isPaused || manifest.isError;
  const manifestPending = manifest.isPending && !manifest.isPaused;
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);
  const retryAssets = useRetryPausedQuery(queryKeys.libraryAssets(showArchived));

  // Only offered where it could work: a 401, 403, 404 or 501 has settled, and a
  // "Try again" over it would be a control that cannot possibly help.
  const anyRecoverableFailure = [...heads.failedAssetIds].some(
    (assetId) => !isTerminalQueryError(heads.failureByAssetId.get(assetId)),
  );

  const createControl = (
    <Tooltip
      content={write.reason ?? `Create a new main concern in ${sectionLabel}`}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={write.reason !== null}
        onClick={() => setCreating(true)}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New main concern
      </Button>
    </Tooltip>
  );

  return (
    <>
      {/* #492's per-section heading and description, now the SHARED one (#908).
          It used to be `NamedSectionHeader`, an `h2` under the page-wide
          "Clinical library" banner that `LibraryRoute` rendered above the rail
          — deliberately, because two `h1`s would have left the document with
          competing top-level headings, and reconciling them was recorded as a
          later ticket's. The banner is gone, so this section's own name is the
          page's `h1`, the breadcrumb says where it sits, and the creation and
          archive controls are the top-right action area the reference puts on
          the same row rather than a toolbar below it. */}
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={
          <>
            {createControl}
            {/* A toggle, in the house form (`AlertMatrixEditor`,
                `LibraryCatalog`): the label stays constant — that is what makes
                `aria-pressed` read as a toggle rather than as a second button —
                and the variant carries the visible state. */}
            <Button
              variant={showArchived ? "primary" : "outline"}
              size="sm"
              aria-pressed={showArchived}
              onClick={() => {
                setShowArchived((value) => !value);
                setPages(1);
              }}
            >
              Show archived
            </Button>
          </>
        }
      />
      <section aria-label={`${sectionLabel} library assets`} className="space-y-4">

      {/* A HELD REFRESH OVER CACHED ROWS IS STILL A FAILED REFRESH.
          `isError` is the SETTLED half only, and it is the half that never
          happens once rows are in hand and the retry is parked: query-core
          keeps `status: "success"` with the previous rows in `data`,
          `fetchStatus: "paused"` and `isError: false`. So this notice stayed
          hidden and the table below went on presenting rows of unknown age as
          current — the one place on this surface where stale data is
          indistinguishable from fresh, because a row carries no read state of
          its own.

          The retry is the cancel-then-refetch one for the same reason the
          no-data branch below uses it: a paused retryer answers a bare
          `refetch()` with its own existing promise and fires nothing, which
          would make the control dead in exactly the state that offers it
          (#215). It is correct for the settled arm too — cancelling an idle
          query is a no-op. */}
      {assets.data ? (
        <StaleDataNotice
          updatedAt={assets.dataUpdatedAt}
          onRetry={retryAssets}
          visible={
            assets.isPaused ||
            (assets.isError && !isTerminalQueryError(assets.error))
          }
        />
      ) : null}

      <PartialDataNotice
        visible={heads.failedAssetIds.size > 0}
        description="Some rows' opening prompts could not be read. Those rows say so below; everything else on this page is unaffected."
        {...(anyRecoverableFailure
          ? {
              action: (
                <Button variant="outline" onClick={heads.retryFailed}>
                  Try again
                </Button>
              ),
            }
          : {})}
      />

      <PartialDataNotice
        visible={roleUnknown}
        description="The Editor Service did not report which document is a main concern's opening prompt, so that column cannot be shown for any row. Everything else on this page is unaffected."
        {...(roleUnknown && !isTerminalQueryError(manifestFailure)
          ? {
              action: (
                <Button variant="outline" onClick={retryManifest}>
                  Try again
                </Button>
              ),
            }
          : {})}
      />

      {assets.isPaused && assets.data === undefined ? (
        // The catalog's own held read, which `isPending` cannot express: it stays
        // `"pending"` while nothing is in flight, so the branch below would put a
        // skeleton here permanently. Only the no-data case is diverted — a pause
        // over rows already in hand keeps showing them, which is the right answer
        // and the one `StaleDataNotice` above gives, now that it tests `isPaused`
        // as well. Until it did, this comment was half true and the other half
        // was the defect: the rows stayed, and nothing said they were stale.
        <NamedReadFailureState
          failure={assets.failureReason}
          isPaused
          retry={retryAssets}
          subject="Library assets"
          title="Library assets could not be loaded"
          description="The Editor Service did not return the shared clinical library."
          deniedDescription="You are not authorized to read the shared clinical library."
          offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
        />
      ) : assets.isPending ? (
        <LoadingState label={`Loading ${sectionLabel} assets`} rows={4} />
      ) : assets.isError &&
        (!assets.data || isTerminalQueryError(assets.error)) ? (
        <QueryErrorState
          error={assets.error}
          subject="Library assets"
          title="Library assets could not be loaded"
          description="The Editor Service did not return the shared clinical library."
          deniedDescription="You are not authorized to read the shared clinical library."
          onRetry={() => void assets.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={`No assets in ${sectionLabel} yet`}
          description={
            showArchived
              ? "Nothing of this section's types exists in the library."
              : "Nothing active is in this section. Archived assets stay hidden until shown."
          }
          action={createControl}
        />
      ) : (
        <Card className="@container overflow-hidden">
          <div
            className={`hidden ${COLUMNS} items-center gap-4 border-b border-border px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground @5xl:grid`}
          >
            <span>Name</span>
            <span>Opening prompt</span>
            <span>Updated</span>
            <span>Adoption</span>
            <span className="sr-only">Actions</span>
          </div>

          {visible.map((asset) => (
            <Row
              key={asset.id}
              asset={asset}
              openingPrompt={openingPromptFor(heads.byAssetId.get(asset.id), openingRole)}
              pending={heads.pendingAssetIds.has(asset.id) || manifestPending}
              failed={heads.failedAssetIds.has(asset.id)}
              roleUnknown={roleUnknown}
              onCopy={() => setCopying(asset)}
              linkRef={(element) => {
                if (element) rowLinks.current.set(asset.id, element);
                else rowLinks.current.delete(asset.id);
              }}
            />
          ))}

          {/* The page boundary is stated, never silent: a list that stops at
              twenty rows without saying so would read as the whole catalog.
              Mounted for as long as paging applies, so the live region is a
              stable node that can announce the final state too. */}
          {paged ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3 text-sm text-muted-foreground">
              <span aria-live="polite">
                {hidden > 0 ? `Showing ${visible.length} of ` : `All `}
                {items.length}{" "}
                {showArchived ? "main concerns, archived included" : "active main concerns"}
                {hidden > 0 ? "" : " shown"}
              </span>
              {hidden > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFocusIndex(visible.length);
                    setPages((value) => value + 1);
                  }}
                >
                  Show {Math.min(hidden, pageSize)} more
                </Button>
              ) : null}
            </div>
          ) : null}
        </Card>
      )}

      {creating ? (
        <NewMainConcernDialog onClose={() => setCreating(false)} />
      ) : null}
      {copying ? (
        <NewMainConcernDialog
          copyFrom={copying}
          onClose={() => setCopying(null)}
        />
      ) : null}
      </section>
    </>
  );
}

/**
 * The text of the asset's opening prompt document, or `null`.
 *
 * Matched on the ROLE the manifest names, and on the language-neutral or first
 * language present — the canonical column shows one sentence, and this
 * repository's assets carry the same document per language.
 */
function openingPromptFor(
  detail: LibraryAssetVersionDetail | undefined,
  role: string | null,
): string | null {
  if (!detail || role === null || !isPartsContent(detail.content)) {
    return null;
  }
  const part = detail.content.parts.find(
    (candidate) => candidate.key === role || candidate.key.endsWith(`/${role}`),
  );
  return part && part.kind === "prompt_text" ? part.text : null;
}

function Row({
  asset,
  openingPrompt,
  pending,
  failed,
  roleUnknown,
  onCopy,
  linkRef,
}: {
  asset: LibraryAsset;
  openingPrompt: string | null;
  pending: boolean;
  /** This asset's head read failed, so the column below is unknown, not empty. */
  failed: boolean;
  /**
   * The manifest read failed, so WHICH document is the opening prompt is
   * unknown — for every row, however well its own head read went.
   */
  roleUnknown: boolean;
  onCopy: () => void;
  /** The row's link, so the catalog can move focus to a newly revealed row. */
  linkRef: (element: HTMLAnchorElement | null) => void;
}) {
  return (
    <div
      className={`relative border-b border-border last:border-b-0 hover:bg-muted/40 @5xl:grid ${COLUMNS} @5xl:items-center @5xl:gap-4`}
    >
      <div className="min-w-0 px-5 py-3.5 text-sm font-medium text-foreground">
        <div className="flex items-center gap-2.5">
          <FileText
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-muted-foreground"
          />
          {/* The row link is stretched over the whole row, and the copy control
              below paints above it — the same arrangement `DataRow` uses so the
              browser hit-tests the button and nothing else. */}
          <Link
            ref={linkRef}
            to={`/library/chief-complaint/${encodeURIComponent(asset.id)}`}
            className="min-w-0 truncate before:absolute before:inset-0 before:content-['']"
          >
            {asset.name}
          </Link>
        </div>
        {/* State and version live in this cell rather than as columns — the
            #492 canonical column set stands, but a list that can hold archived
            rows must mark them (#647). Both are server truth: the badges read
            `capabilities`, the label is the head version. On their OWN line:
            sharing the name's line is what squeezed the link to nothing once
            the grid activated. */}
        <div className="mt-1 flex flex-wrap items-center gap-2 pl-[1.625rem]">
          <AssetStateBadges capabilities={asset.capabilities} />
          <span className="shrink-0 font-mono text-xs text-muted-foreground">
            {asset.version}
          </span>
        </div>
      </div>

      <div className="truncate px-5 pb-2 text-sm @5xl:py-3.5">
        {(failed || roleUnknown) && openingPrompt === null ? (
          <span className="text-destructive">
            Opening prompt could not be read
          </span>
        ) : (
          <span className="text-muted-foreground">
            {pending && openingPrompt === null
              ? "Loading…"
              : (openingPrompt ?? EM_DASH)}
          </span>
        )}
      </div>

      {/* Two facts, stated separately: `updatedAt` moves on every head change —
          a version somebody else published, an archive, a restore — while
          `createdBy` is the creator and nobody else. "DATE by CREATOR" read as
          one sentence attributed the latest change to the wrong person the
          moment a second principal touched the asset (#647: exact provenance,
          no fabricated metadata). The service publishes no last-change actor
          on the catalog read, so none is shown. */}
      <div className="px-5 pb-2 text-sm text-muted-foreground @5xl:py-3.5">
        <div>
          <span className="@5xl:hidden">Last updated </span>
          {formatDate(asset.updatedAt)}
        </div>
        <div className="text-xs">Created by {asset.createdBy}</div>
      </div>

      <div className="px-5 pb-2 text-sm text-muted-foreground @5xl:py-3.5">
        {asset.capabilities.adoptionCount}{" "}
        {asset.capabilities.adoptionCount === 1 ? "project" : "projects"}
      </div>

      <div className="flex items-center justify-end gap-1 px-5 pb-3.5 @5xl:py-3.5">
        {asset.capabilities.seeded ? (
          // The canonical reference shows this as a BADGE explaining why the
          // seeded row cannot be archived. Here it is the control that performs
          // the copy the service's own refusal instructs — the same words, and
          // an action rather than a dead label.
          <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
            <Button
              variant="ghost"
              size="sm"
              className="relative"
              onClick={onCopy}
            >
              Create editable copy
            </Button>
          </Tooltip>
        ) : null}
        <ChevronRight
          aria-hidden="true"
          className="h-4 w-4 text-muted-foreground"
        />
      </div>
    </div>
  );
}
